import type { ReactNode } from 'react'

import { Button } from '@/components/ui/Button'
import { Skeleton } from '@/components/ui/Feedback'
import { CompanyAvatar } from '@/features/applications/ApplicationsTable'
import { cn, relativeTime } from '@/lib/utils'
import type { AutoApplySearchResult, JobMatch, PageInfo } from '@/types/api'
import { MatchRing } from '../JobCard'
import { formatNumber, sourceLabel, workplaceLabel } from './filters'

/** "186 jobs ready to apply · 1,240 more openings fit in the career-page pool · Naukri 40 · Company careers 146". */
export function CountLine({
  totals,
  sourceLabels,
}: {
  totals: AutoApplySearchResult['totals']
  sourceLabels: Record<string, string>
}) {
  const sources = Object.entries(totals.bySource ?? {})
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
  const pool = totals.poolEligible != null && totals.poolEligible > 0
  return (
    <div className="min-w-0" aria-live="polite">
      <p className="text-[16px] font-semibold text-ink">
        <span className="num">{formatNumber(totals.ready)}</span> job{totals.ready === 1 ? '' : 's'} ready to apply
      </p>
      {(pool || sources.length > 0) && (
        <p className="mt-0.5 text-[13px] leading-relaxed text-ink-muted">
          {pool && (
            <span title="Openings on company career pages that fit these filters. The best ones join your list as we search.">
              <span className="num">{formatNumber(totals.poolEligible!)}</span> more openings fit in the career-page pool
            </span>
          )}
          {sources.map(([key, count], index) => (
            <span key={key} className="whitespace-nowrap">
              {(pool || index > 0) && <Dot />}
              {sourceLabel(key, sourceLabels)} <span className="num text-ink-soft">{formatNumber(count)}</span>
            </span>
          ))}
        </p>
      )}
    </div>
  )
}

function Dot() {
  return <span className="mx-1.5 text-ink-faint" aria-hidden="true">·</span>
}

/** While the pool search runs: the list stays, this line says more may come. */
export function RefreshBanner({ refreshing, error }: { refreshing: boolean; error: string | null }) {
  if (refreshing) {
    return (
      <div role="status" className="flex items-center gap-2.5 rounded-field bg-info/10 px-3 py-2 text-[13px] text-ink-soft">
        <svg className="h-4 w-4 shrink-0 animate-spin text-info" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
          <path d="M14 8a6 6 0 0 0-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <span className="min-w-0">
          Searching the career-page pool for more openings… <span className="text-ink-muted">This takes a few seconds.</span>
        </span>
      </div>
    )
  }
  if (error) {
    return (
      <p className="rounded-field bg-surface-muted px-3 py-2 text-[12.5px] text-ink-muted">
        We could not search the career-page pool just now, so these are your saved matches. Change a filter to try again.
      </p>
    )
  }
  return null
}

export function ResultsSkeleton() {
  return (
    <div className="card divide-y divide-line overflow-hidden">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="flex items-center gap-3.5 px-4 py-4 sm:px-5">
          <Skeleton className="h-4 w-4 rounded-[4px]" />
          <Skeleton className="hidden h-9 w-9 rounded-[9px] sm:block" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-9 w-9 rounded-full" />
        </div>
      ))}
    </div>
  )
}

/** The results card: select-all header, rows, pages. */
export function ResultsList({
  items,
  pageInfo,
  selected,
  onToggle,
  onSelectPage,
  onClearSelection,
  onPage,
  dim,
  sourceLabels,
  header,
}: {
  items: JobMatch[]
  pageInfo: PageInfo
  selected: ReadonlyMap<string, JobMatch>
  onToggle: (match: JobMatch) => void
  onSelectPage: (select: boolean) => void
  onClearSelection: () => void
  onPage: (page: number) => void
  /** Older results shown while new ones load. */
  dim: boolean
  sourceLabels: Record<string, string>
  header?: ReactNode
}) {
  const pageIds = items.map((m) => m.job.id)
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.has(id))
  const someOnPage = pageIds.some((id) => selected.has(id))
  const start = (pageInfo.page - 1) * pageInfo.pageSize + 1
  const end = Math.min(pageInfo.page * pageInfo.pageSize, pageInfo.totalItems)

  return (
    <section className={cn('card min-w-0 overflow-hidden transition-opacity', dim && 'opacity-60')} aria-busy={dim}>
      {header}
      <div className="flex min-h-[48px] items-center gap-3 border-b border-line px-4 py-2 sm:px-5">
        <label className="flex min-w-0 cursor-pointer items-center gap-2.5 text-[13px] text-ink-soft">
          <input
            type="checkbox"
            checked={allOnPage}
            ref={(el) => {
              if (el) el.indeterminate = someOnPage && !allOnPage
            }}
            onChange={(event) => onSelectPage(event.target.checked)}
            className="h-4 w-4 shrink-0 cursor-pointer accent-ink"
          />
          Select all on this page
        </label>
        <span className="ml-auto flex shrink-0 items-center gap-2 text-[12.5px] text-ink-muted">
          {selected.size > 0 ? (
            <>
              <span className="num hidden font-medium text-ink sm:inline">{selected.size} selected</span>
              <button
                type="button"
                onClick={onClearSelection}
                className="tap rounded-[6px] px-1.5 py-1 font-medium text-ink-soft hover:bg-surface-muted hover:text-ink"
              >
                Clear selection
              </button>
            </>
          ) : (
            <span className="num hidden xs:inline">
              {formatNumber(start)}–{formatNumber(end)} of {formatNumber(pageInfo.totalItems)}
            </span>
          )}
        </span>
      </div>

      <ul className="divide-y divide-line">
        {items.map((match) => (
          <ResultRow
            key={match.id}
            match={match}
            checked={selected.has(match.job.id)}
            onToggle={() => onToggle(match)}
            sourceLabels={sourceLabels}
          />
        ))}
      </ul>

      {pageInfo.totalPages > 1 && (
        <nav className="flex items-center justify-between gap-2 border-t border-line px-3 py-2.5 sm:px-4" aria-label="Pages">
          <Button
            variant="secondary"
            size="sm"
            className="h-10 sm:h-9"
            disabled={!pageInfo.hasPrevious}
            onClick={() => onPage(pageInfo.page - 1)}
            icon={<Chevron dir="left" />}
          >
            Prev
          </Button>
          <p className="min-w-0 truncate text-center text-[13px] text-ink-muted">
            Page <span className="num font-medium text-ink">{pageInfo.page}</span> of{' '}
            <span className="num">{formatNumber(pageInfo.totalPages)}</span>
          </p>
          <Button
            variant="secondary"
            size="sm"
            className="h-10 sm:h-9"
            disabled={!pageInfo.hasNext}
            onClick={() => onPage(pageInfo.page + 1)}
            trailing={<Chevron dir="right" />}
          >
            Next
          </Button>
        </nav>
      )}
    </section>
  )
}

/** Checkbox · company · title · where · badges · score · View. The whole row toggles the checkbox. */
function ResultRow({
  match,
  checked,
  onToggle,
  sourceLabels,
}: {
  match: JobMatch
  checked: boolean
  onToggle: () => void
  sourceLabels: Record<string, string>
}) {
  const { job } = match
  const source = job.board?.name ?? sourceLabel(job.source, sourceLabels)
  const workplace = workplaceLabel(job.workplaceType)
  const posted = job.postedAt ? relativeTime(job.postedAt) : null
  const id = `aa-job-${job.id}`

  return (
    <li className={cn('flex items-start gap-3 px-4 py-3 transition-colors sm:items-center sm:px-5', checked ? 'bg-brand-soft/40' : 'hover:bg-surface-muted/50')}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-ink sm:mt-0"
        aria-label={`Select ${job.title} at ${job.companyName}`}
      />
      <label htmlFor={id} className="flex min-w-0 flex-1 cursor-pointer items-start gap-3 sm:items-center">
        <span className="hidden sm:block">
          <CompanyAvatar name={job.companyName} logoUrl={job.company?.logoUrl} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="line-clamp-2 block text-[14.5px] font-semibold leading-snug text-ink">{job.title}</span>
          <span className="mt-0.5 block truncate text-[13px] text-ink-muted">
            <span className="font-medium text-ink-soft">{job.companyName}</span>
            {job.location ? ` · ${job.location}` : ''}
          </span>
          <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {workplace && <Badge tone={job.workplaceType === 'remote' ? 'brand' : 'neutral'}>{workplace}</Badge>}
            <Badge>{source}</Badge>
            {posted && <span className="whitespace-nowrap text-[12px] text-ink-faint">{posted}</span>}
          </span>
        </span>
        <MatchRing score={match.matchScore} size={40} />
      </label>
      <a
        href={job.applyUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`View posting for ${job.title} at ${job.companyName}`}
        className="inline-flex h-9 shrink-0 items-center gap-1 rounded-field px-2 text-[13px] font-medium text-ink-soft transition-colors hover:bg-surface-muted hover:text-ink sm:px-2.5"
      >
        <span className="hidden sm:inline">View</span>
        <svg className="h-4 w-4 sm:h-3.5 sm:w-3.5" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M6.5 3.5h-2a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1v-2M9.5 3h3.5v3.5M13 3 7.5 8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </a>
    </li>
  )
}

function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'brand' }) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center whitespace-nowrap rounded-[6px] px-1.5 py-0.5 text-[11.5px]',
        tone === 'brand' ? 'bg-brand-soft font-medium text-brand' : 'bg-surface-muted text-ink-soft',
      )}
    >
      {children}
    </span>
  )
}

function Chevron({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d={dir === 'left' ? 'm7.5 2.5-3.5 3.5 3.5 3.5' : 'm4.5 2.5 3.5 3.5-3.5 3.5'} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
